import axios from 'axios'
import api from './client'
import type {
  MerchantBatchDetailRow,
  MerchantBatchSummaryRow,
  MerchantPaymentDetailRow,
  MerchantPaymentSummaryRow,
  MerchantReportingResponse,
  MerchantStatementsResponse,
} from '../api'

export const merchantReportingApi = {
  syncMetrics: async (merchantId: string): Promise<{
    success: boolean
    monthly_volume: number
    ytd_volume: number
    monthly_transaction_count: number
    monthly_refund_amount: number
    chargeback_ratio: number
    last_batch_at: string | null
  }> => {
    const response = await api.post(`/merchants/${merchantId}/reporting/sync-metrics`)
    return response.data
  },

  batchSummary: async (
    merchantId: string,
    params: { from: string; to: string; page?: number; per_page?: number },
  ): Promise<MerchantReportingResponse<MerchantBatchSummaryRow>> => {
    const response = await api.get<MerchantReportingResponse<MerchantBatchSummaryRow>>(
      `/merchants/${merchantId}/reporting/batches`,
      { params },
    )
    return response.data
  },

  batchDetail: async (
    merchantId: string,
    batchNumber: string,
    params: { report_date: string; page?: number; per_page?: number },
  ): Promise<MerchantReportingResponse<MerchantBatchDetailRow>> => {
    const response = await api.get<MerchantReportingResponse<MerchantBatchDetailRow>>(
      `/merchants/${merchantId}/reporting/batches/${encodeURIComponent(batchNumber)}`,
      { params },
    )
    return response.data
  },

  paymentSummary: async (
    merchantId: string,
    params: { from: string; to: string; page?: number; per_page?: number },
  ): Promise<MerchantReportingResponse<MerchantPaymentSummaryRow>> => {
    const response = await api.get<MerchantReportingResponse<MerchantPaymentSummaryRow>>(
      `/merchants/${merchantId}/reporting/payments`,
      { params },
    )
    return response.data
  },

  paymentDetail: async (
    merchantId: string,
    params: { from: string; to: string; page?: number; per_page?: number },
  ): Promise<MerchantReportingResponse<MerchantPaymentDetailRow>> => {
    const response = await api.get<MerchantReportingResponse<MerchantPaymentDetailRow>>(
      `/merchants/${merchantId}/reporting/payments/detail`,
      { params },
    )
    return response.data
  },

  voucher: async (
    merchantId: string,
    transactionId: string,
    params: { report_date: string },
  ): Promise<{ success: boolean; pdf: string | null; errorMessage: string | null }> => {
    const response = await api.get<{ success: boolean; pdf: string | null; errorMessage: string | null }>(
      `/merchants/${merchantId}/reporting/vouchers/${encodeURIComponent(transactionId)}`,
      { params },
    )
    return response.data
  },

  statements: async (merchantId: string): Promise<MerchantStatementsResponse> => {
    const response = await api.get<MerchantStatementsResponse>(`/merchants/${merchantId}/statements`)
    return response.data
  },

  statementDownload: async (merchantId: string, statementId: string): Promise<Blob> => {
    try {
      const response = await api.get<Blob>(
        `/merchants/${merchantId}/statements/${encodeURIComponent(statementId)}`,
        { responseType: 'blob' },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
        try {
          const parsed = JSON.parse(await error.response.data.text()) as unknown
          const responseWithData = error.response as { data: unknown }
          responseWithData.data = parsed
        } catch {
          // Leave non-JSON error blobs untouched; the UI will use its fallback.
        }
      }
      throw error
    }
  },
}
